import { Breadcrumbs } from "@components/atoms/Breadcrumbs"
import { Chip } from "@components/atoms/Chip"
import { Heading } from "@components/atoms/Heading"
import { Swiper, SwiperSlide } from "@components/atoms/Swiper"
import { Chips, PageContent } from "@components/atoms/single/Shared"
import { Col, Container, Grid } from "@components/layout/Grid"
import { VSpace } from "@components/layout/Spacer"
import { useIsMobile } from "@hooks/useViewportDerived"
import { ContentBlocks } from "eddev/blocks"
import { defineView } from "eddev/views"
import { useMemo } from "react"
import { DocumentCardFragment } from "../types.graphql"
import { Card } from "@components/molecules/Cards"
import { RsrpMobileHeader, RsrpSidebar } from "@components/atoms/single/Sidebars"

export default defineView("single-rsrp-project", (props) => {
  const isMobile = useIsMobile()

  const chips = useMemo(() => {
    return props.rsrpProject?.rsrpFocusAreas?.nodes?.concat(props.rsrpProject.rsrpTopics?.nodes ?? []) ?? []
  }, [props])

  return (
    <>
      <Breadcrumbs
        uri={"/research-data/road-safety-research-program"}
        page={props.breadcrumbsPage}
        nonLinkExtra="projects"
      />
      <Container>
        <Grid>
          {isMobile && (
            <Col initial={12}>
              {props.rsrpProject?.projectMeta && (
                <RsrpMobileHeader
                  title={props.rsrpProject?.title ?? ""}
                  chips={chips}
                  projectMeta={props.rsrpProject.projectMeta}
                />
              )}
            </Col>
          )}

          {!isMobile && (
            <Col initial={12}>
              <VSpace initial={10} />
            </Col>
          )}

          <Col initial={12} tablet={8} laptop={7}>
            <PageContent>
              {!isMobile && (
                <>
                  <Heading
                    as="h1"
                    dangerouslySetInnerHTML={{
                      __html: `${props.rsrpProject?.title}`,
                    }}
                  />

                  {chips && (
                    <>
                      <VSpace initial={3} />
                      <Chips>
                        {chips.map((chip, i) => (
                          <Chip color="outline" key={i}>
                            {chip.name}
                          </Chip>
                        ))}
                      </Chips>
                    </>
                  )}

                  <VSpace initial={5} laptop={8} />
                </>
              )}

              <div className="blocks">
                <ContentBlocks blocks={props.rsrpProject?.contentBlocks} />
              </div>
            </PageContent>
          </Col>

          {!isMobile && (
            <>
              <Col initial={12} tablet={1} laptop={2} />
              <Col initial={12} tablet={3} laptop={3}>
                {props.rsrpProject?.projectMeta && (
                  <RsrpSidebar chips={chips} projectMeta={props.rsrpProject.projectMeta} />
                )}
              </Col>
              <Col initial={12} tablet={1} laptop={2} />
            </>
          )}
        </Grid>

        {props.rsrpProject?.projectMeta?.research && (
          <DocumentSwiper
            title="Research"
            documents={props.rsrpProject.projectMeta.research as DocumentCardFragment[]}
          />
        )}
        {props.rsrpProject?.projectMeta?.tools && (
          <DocumentSwiper title="Tools" documents={props.rsrpProject.projectMeta.tools as DocumentCardFragment[]} />
        )}
      </Container>
    </>
  )
})

const DocumentSwiper = ({ title, documents }: { title: string; documents: DocumentCardFragment[] }) => (
  <>
    <Grid className="gap-y-4 [&>div]:flex [&>div]:items-center [&>div]:py-4">
      <Col initial={12}>
        <Heading as="h5">{title}</Heading>
      </Col>
    </Grid>
    <Grid>
      <Col initial={12}>
        <style
          dangerouslySetInnerHTML={{
            __html: `
          .rsrp-project-swiper {
            overflow: visible !important;
            padding-bottom: 40px;

            div[class^='swiper-button'] {
              top: auto;
              bottom: 0;
              right: 0;
              left: auto;
              width: 27px;
              height: 27px;
              color: #222222;
              box-shadow: inset 0px 0px 0px 1px #222222;
              border-radius: 6px;
              transition: all .35s cubic-bezier(.19,1,.22,1);

              &:after {
                font-size: 16px !important;
              }

              svg {
                width: 5px;
                height: auto;

                path {
                  stroke: currentColor;
                  stroke-width: 2px;
                }
              }

              &:hover {
                background: #5E5E5E;
                box-shadow: inset 0px 0px 0px 1px #5E5E5E;
                color: #FFFFFF;
              }

              &:active {
                background: #222222;
                box-shadow: inset 0px 0px 0px 1px #222222;
                color: #FFFFFF;
              }

              &:disabled {
                background: transparent;
                box-shadow: inset 0px 0px 0px 1px #8E8E8E;
                color: #8E8E8E;
              }
            }

            .swiper-button-prev {
              margin-right: 36px;
            }
          }

          @media only screen and (min-width: 900px) {
            .rsrp-project-swiper {
              padding-bottom: 0;

              div[class^='swiper-button'] {
                bottom: auto;
                top: -18px;
              }
            }
          }

          @media only screen and (min-width: 1200px) {
            .rsrp-project-swiper {
              div[class^='swiper-button'] {
                bottom: auto;
                top: -34px;
                width: 40px;
                height: 40px;
              }

              .swiper-button-prev {
                margin-right: 50px;
              }
            }
          }
        `,
          }}
        />
        <Swiper
          className="rsrp-project-swiper"
          spaceBetween={24}
          autoplay={{
            delay: 5000,
          }}
          navigation
          breakpoints={{
            900: {
              slidesPerView: 2,
            },
            0: {
              slidesPerView: 1.15,
            },
          }}
        >
          {documents?.map((document: DocumentCardFragment, i: number) => (
            <SwiperSlide key={i} style={{ display: "flex", height: "auto" }}>
              <Card type="document" {...document} />
            </SwiperSlide>
          ))}
        </Swiper>

        <VSpace initial={8} />
      </Col>
    </Grid>
  </>
)
